import { DeleteIcon, EditIcon, LinkIcon, LocationIcon as LocationSvg } from '@/assets/icons'
import { mutedGrey } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'
import { TestIds } from '@/testIds'
import { AttachmentSection } from '@components/Attachments'
import BackLink from '@components/BackLink'
import { ideaCard } from '@components/Chat/core'
import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import { ErrorNote } from '@components/Notice'
import { PageColumn, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useDeleteIdea, useIdeas, useTripMembers, useUpdateIdea } from '@hooks'
import { getErrorMessage } from '@lib/apiError'
import { normalizeExternalUrl } from '@lib/utils'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import {
	ActionButtons,
	DetailBody,
	DetailCard,
	DetailHeader,
	DetailTitle,
	IconButton,
	LoadingContainer,
	LocationIcon,
	LocationPrimary,
	LocationSecondary,
	LocationSection,
	LocationText,
	MapButton,
	MetaSection,
	NotesContent,
	NotFoundMessage,
	SectionLabel,
	TitleRow,
} from '../../Accommodations/AccommodationDetailPage/AccommodationDetailPage.styles'
import { getGoogleMapsUrl } from '../../Accommodations/accommodationHelpers'
import EditIdeaModal from '../EditIdeaModal'
import { ideaThumbnailUrl } from '../ideaFilters'
import { IdeaDraft, toUpdatePayload } from '../ideaForm'
import { CardLinks, CardTags, LinkInfo, LinkPreviewCard, LinkSource, LinkThumbnail, LinkUrl, Tag } from '../ideaLinks.styles'
import { BOOKMARK_CATEGORY_LABELS } from '../utils'
import { CategoryPill, Discussion, HeroImage } from './IdeaDetailPage.styles'

const formatSourceName = (source: string): string => {
	if (source === 'GoogleMaps') return 'Google Maps'
	if (source === 'AppleMaps') return 'Apple Maps'
	return source
}

/**
 * One trip Idea at `/trips/:tripId/ideas/:ideaId`, read-only like iOS's idea details:
 * its picture, category, name, location, description, links, tags and attachments, who
 * added it, and its Discussion. The pencil opens the edit form; the trash deletes it.
 * Ideas open here from their grid tile and from a card in the Chat.
 */
const IdeaDetailPage: React.FC = () => {
	const { tripId, ideaId } = useParams<{ tripId: string; ideaId: string }>()
	const navigate = useNavigate()
	const { data: ideasData, isLoading, error } = useIdeas(tripId)
	const { data: membersData } = useTripMembers(tripId)
	const updateIdea = useUpdateIdea()
	const deleteIdea = useDeleteIdea()
	const [isEditOpen, setIsEditOpen] = useState(false)
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)

	const idea = ideasData?.ideas.find(candidate => candidate.id === Number(ideaId))
	const ideasPath = `/trips/${tripId}/ideas`

	if (isLoading) return <PageLoader label="Loading idea…" />
	if (error) return <ErrorNote>Error loading idea. Please try again.</ErrorNote>
	if (!tripId || !idea) {
		return (
			<LoadingContainer>
				<NotFoundMessage>Idea not found</NotFoundMessage>
			</LoadingContainer>
		)
	}

	const members = membersData?.members ?? []
	const createdBy = members.find(member => member.id === idea.createdByUserId)
	const thumbnail = ideaThumbnailUrl(idea)
	const mapsUrl = getGoogleMapsUrl(idea.location)
	const links = idea.links ?? []
	const tags = idea.tags ?? []
	const fileIds = idea.fileIds ?? []

	const handleUpdate = async (draft: IdeaDraft) => {
		try {
			await updateIdea.mutateAsync({ tripId, ideaId: String(idea.id), ideaData: toUpdatePayload(draft) })
			toast.success('Idea updated successfully!')
		} catch (updateError) {
			toast.error(getErrorMessage(updateError, 'Failed to update idea. Please try again.'))
		}
	}

	const handleDelete = async () => {
		try {
			await deleteIdea.mutateAsync({ tripId, ideaId: String(idea.id) })
			setIsDeleteOpen(false)
			toast.success('Idea deleted successfully!')
			navigate(ideasPath)
		} catch (deleteError) {
			toast.error(getErrorMessage(deleteError, 'Failed to delete idea. Please try again.'))
		}
	}

	return (
		<PageScroll>
			<PageColumn>
				<DetailCard>
					<DetailHeader>
						<TitleRow>
							<BackLink path={ideasPath} label="Ideas" />
							<DetailTitle data-testid={TestIds.idea.detailsName}>{idea.name}</DetailTitle>
						</TitleRow>
						<ActionButtons>
							<IconButton onClick={() => setIsEditOpen(true)} title="Edit" aria-label="Edit" data-testid={TestIds.idea.editButton}>
								<EditIcon sx={{ fontSize: iconSizes.md }} />
							</IconButton>
							<IconButton
								$variant="danger"
								onClick={() => setIsDeleteOpen(true)}
								title="Delete"
								aria-label="Delete"
								data-testid={TestIds.idea.deleteButton}
							>
								<DeleteIcon sx={{ fontSize: iconSizes.md }} />
							</IconButton>
						</ActionButtons>
					</DetailHeader>

					<DetailBody>
						{thumbnail && <HeroImage src={thumbnail} alt={idea.name} />}
						<CategoryPill>{BOOKMARK_CATEGORY_LABELS[idea.category]}</CategoryPill>

						{idea.location && (
							<LocationSection>
								<LocationIcon>
									<LocationSvg sx={{ fontSize: iconSizes.lg }} />
								</LocationIcon>
								<LocationText>
									<LocationPrimary>{idea.location.primaryText || idea.location.fullText}</LocationPrimary>
									{idea.location.secondaryText && <LocationSecondary>{idea.location.secondaryText}</LocationSecondary>}
								</LocationText>
								{mapsUrl && (
									<MapButton href={mapsUrl} target="_blank" rel="noopener noreferrer">
										View Map
									</MapButton>
								)}
							</LocationSection>
						)}

						{idea.description && (
							<div>
								<SectionLabel>Description</SectionLabel>
								<NotesContent>{idea.description}</NotesContent>
							</div>
						)}

						{links.length > 0 && (
							<div>
								<SectionLabel>Links ({links.length})</SectionLabel>
								<CardLinks>
									{links.map(link => (
										<LinkPreviewCard
											key={link.id}
											href={normalizeExternalUrl(link.url)}
											target="_blank"
											rel="noopener noreferrer"
											$clickable
										>
											<LinkThumbnail src={link.thumbnail || undefined}>
												{!link.thumbnail && <LinkIcon htmlColor={mutedGrey} />}
											</LinkThumbnail>
											<LinkInfo>
												<LinkSource>{formatSourceName(link.source)}</LinkSource>
												<LinkUrl>{link.url}</LinkUrl>
											</LinkInfo>
										</LinkPreviewCard>
									))}
								</CardLinks>
							</div>
						)}

						{tags.length > 0 && (
							<div>
								<SectionLabel>Tags</SectionLabel>
								<CardTags>
									{tags.map(tag => (
										<Tag key={tag}>{tag}</Tag>
									))}
								</CardTags>
							</div>
						)}

						{fileIds.length > 0 && (
							<div>
								<SectionLabel>Attachments ({fileIds.length})</SectionLabel>
								<AttachmentSection tripId={tripId} resourceType="IDEA" resourceId={idea.id} fileIds={fileIds} readOnly />
							</div>
						)}

						{createdBy && (
							<MetaSection>
								<span>
									Added by {[createdBy.firstName, createdBy.lastName].filter(Boolean).join(' ')}
								</span>
							</MetaSection>
						)}
					</DetailBody>
				</DetailCard>

				<Discussion tripId={tripId} resource={ideaCard(idea)} />
			</PageColumn>

			<EditIdeaModal
				isOpen={isEditOpen}
				onClose={() => setIsEditOpen(false)}
				onSubmit={handleUpdate}
				idea={idea}
			/>

			<ConfirmationModal
				isOpen={isDeleteOpen}
				onClose={() => setIsDeleteOpen(false)}
				onConfirm={handleDelete}
				title="Delete Idea"
				message={
					<>
						Are you sure you want to delete &apos;<DeletionTarget>{idea.name}</DeletionTarget>&apos;?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</PageScroll>
	)
}

export default IdeaDetailPage
